/**
 * Shared member display components used by Feed MembersAndKinPanel 
 * and Community Modal User Finder.
 */
import React from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Avatar,
  Badge,
  Icon
} from '@chakra-ui/react';
import { FaStar } from 'react-icons/fa';
import { memberLevelRank } from '../../utils/burrowRanks';

// ─── Tier accent colors ─────────────────────────────────────────────
export const getTierAccent = (tier) => {
  switch (tier) {
    case 'vip':         return { color: '#F6E05E', glow: 'rgba(246,224,94,0.3)', hasTier: true };
    case 'contributor': return { color: '#FC8181', glow: 'rgba(252,129,129,0.3)', hasTier: true };
    case 'supporter':   return { color: '#63B3ED', glow: 'rgba(99,179,237,0.3)', hasTier: true };
    default:            return { color: '#A0AEC0', glow: 'transparent', hasTier: false };
  }
};

// ─── Online indicator dot ───────────────────────────────────────────
export const OnlineDot = () => (
  <Box
    position="absolute"
    bottom="1px"
    right="1px"
    w="10px"
    h="10px"
    bg="green.400"
    borderRadius="full"
    border="2px solid"
    borderColor="gray.800"
    boxShadow="0 0 6px rgba(72,187,120,0.6)"
  />
);

// ─── Notification badge ─────────────────────────────────────────────
export const CountBadge = ({ count, ...props }) => (
  <Box
    position="absolute"
    top="-4px"
    right="-4px"
    bg="red.500"
    color="white"
    fontSize="9px"
    fontWeight={700}
    borderRadius="full"
    minW="16px"
    h="16px"
    display="flex"
    alignItems="center"
    justifyContent="center"
    px="4px"
    border="2px solid"
    borderColor="gray.900"
    {...props}
  >
    {count}
  </Box>
);

// ─── Respect stars row ──────────────────────────────────────────────
export const RespectStars = ({ count = 0, size = '10px', spacing = '1px' }) => (
  <HStack spacing={spacing}>
    {[1, 2, 3, 4, 5].map(i => (
      <Icon
        key={i}
        as={FaStar}
        boxSize={size}
        color={i <= count ? '#F6E05E' : 'whiteAlpha.200'}
        filter={i <= count ? 'drop-shadow(0 0 3px rgba(246,224,94,0.5))' : 'none'}
      />
    ))}
  </HStack>
);

// ─── Orbiting stars around avatar ───────────────────────────────────
const ORBIT_POSITIONS = [
  { top: '-3px', left: '50%', transform: 'translateX(-50%)' },
  { top: '4px',  right: '-1px' },
  { bottom: '4px', right: '-1px' },
  { bottom: '-3px', left: '50%', transform: 'translateX(-50%)' },
  { bottom: '4px', left: '-1px' },
  { top: '4px',  left: '-1px' },
];

export const OrbitingStars = ({ count = 0, size = '7px' }) => (
  <>
    {ORBIT_POSITIONS.slice(0, Math.min(count, 6)).map((pos, i) => (
      <Box key={i} position="absolute" {...pos} zIndex={3}>
        <Icon
          as={FaStar}
          boxSize={size}
          color="#F6E05E"
          filter="drop-shadow(0 0 4px rgba(246,224,94,0.7))"
        />
      </Box>
    ))}
  </>
);

// ─── Member card (grid view) ────────────────────────────────────────
export const MemberCard = ({ member, onClick, isKin: isKinMember = false }) => {
  const accent = getTierAccent(member.membershipTier);
  const tierLabel = member.membershipTier || 'base';
  const borderGradient = isKinMember
    ? 'linear(135deg, #F6E05E, #ED8936, #F6E05E)'
    : `linear(135deg, ${accent.color}, rgba(237,137,54,0.6), ${accent.color})`;
  const borderOpacity = isKinMember ? 0.85 : 0.6;
  const stripGradient = isKinMember
    ? 'linear(to-r, #F6E05E, #ED8936, #F6E05E)'
    : `linear(to-r, ${accent.color}, #ED8936, ${accent.color})`;

  return (
    <Box
      position="relative"
      borderRadius="xl"
      cursor="pointer"
      _hover={{
        transform: 'translateY(-3px)',
        shadow: isKinMember
          ? '0 8px 25px rgba(0,0,0,0.4), 0 0 20px rgba(246,224,94,0.25)'
          : `0 8px 25px rgba(0,0,0,0.4), 0 0 15px ${accent.glow}`,
        '& .card-strip': { opacity: 1 }
      }}
      transition="all 0.25s ease"
      onClick={() => onClick(member)}
      overflow="hidden"
    >
      {/* Gradient border effect */}
      <Box
        position="absolute"
        inset={0}
        borderRadius="xl"
        p="1px"
        bgGradient={borderGradient}
        opacity={borderOpacity}
      />

      {/* Inner card */}
      <Box
        position="relative"
        bg="gray.800"
        borderRadius="xl"
        m="1px"
        p={3}
        textAlign="center"
      >
        {/* Top accent strip */}
        <Box
          className="card-strip"
          position="absolute"
          top={0}
          left={0}
          right={0}
          h={isKinMember ? '4px' : '3px'}
          bgGradient={stripGradient}
          opacity={isKinMember ? 0.8 : 0.5}
          transition="opacity 0.25s"
          borderTopRadius="xl"
        />

        {/* Respect stars — top left */}
        {(member.respectCount || 0) > 0 && (member.respectCount || 0) <= 5 && (
          <Box position="absolute" top="7px" left="8px" zIndex={2}>
            <RespectStars count={member.respectCount} size="9px" spacing="1px" />
          </Box>
        )}

        {/* KIN badge — top right */}
        {isKinMember && (
          <Badge
            position="absolute"
            top="6px"
            right="6px"
            bg="rgba(246,224,94,0.18)"
            color="#F6E05E"
            fontSize="7px"
            fontWeight={800}
            px="6px"
            py="2px"
            borderRadius="full"
            letterSpacing="1.2px"
            border="1px solid rgba(246,224,94,0.3)"
            zIndex={2}
            lineHeight={1}
          >
            KIN
          </Badge>
        )}

        <VStack spacing={2} pt={1}>
          {/* Avatar with online ring */}
          <Box position="relative">
            <Avatar
              size="md"
              src={member.userAvatar}
              name={member.burrowerId}
              border={member.isOnline ? '2.5px solid' : 'none'}
              borderColor={member.isOnline ? 'green.400' : undefined}
              boxShadow={member.isOnline
                ? '0 0 10px rgba(72,187,120,0.4)'
                : accent.hasTier
                  ? '0 0 10px rgba(246,224,94,0.5), 0 0 20px rgba(246,224,94,0.2)'
                  : 'none'
              }
            />
            {member.isOnline && <OnlineDot />}
          </Box>

          {/* Username */}
          <Text color="white" fontSize="sm" fontWeight={600} isTruncated maxW="100%" lineHeight={1.3}>
            {member.burrowerId}
          </Text>

          {/* Tier badge */}
          <Badge
            bg={`${accent.color}22`}
            color={accent.color}
            fontSize="7px"
            fontWeight={700}
            px="8px"
            py="1px"
            borderRadius="full"
            textTransform="uppercase"
            letterSpacing="0.5px"
            border="1px solid"
            borderColor={`${accent.color}33`}
          >
            {tierLabel}
          </Badge>

          {/* Level + Respect stats */}
          <HStack spacing={3} fontSize="9px" color="gray.500" fontWeight={500}>
            <Text>
              <Text as="span" color="#F6E05E" fontWeight={700}>{memberLevelRank(member).name}</Text>
            </Text>
            <Box w="3px" h="3px" bg="gray.600" borderRadius="full" />
            <Text>
              <Text as="span" color="#48BB78" fontWeight={700}>{member.respectCount || 0}</Text> Resp
            </Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};
